import { createClient } from '@supabase/supabase-js'

/**
 * Supabase is used for identity ONLY.
 *
 * It is not a data client here: `etl/db/0005_lockdown.sql` revokes all table
 * access from `anon` and `authenticated`, so this key cannot read a single row.
 * That is deliberate — PostgREST is a generic query interface, and a key that
 * ships in a browser bundle must not be able to page through a paywalled
 * dataset. All data comes from the backend, which verifies the JWT this client
 * issues.
 */

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

/**
 * Public sign-up. Off unless explicitly enabled, because this deployment is
 * meant to have a known set of accounts. Note this only hides the UI: the
 * authoritative switch is Supabase's own "allow new users to sign up" setting
 * (Authentication -> Providers -> Email), which stops registrations hitting the
 * API at all. Turn both off.
 */
export const ALLOW_SIGNUP = import.meta.env.VITE_ALLOW_SIGNUP === 'true'

/** Dev escape hatch, mirroring the backend's `auth_disabled`. Never in prod. */
export const AUTH_DISABLED =
  import.meta.env.VITE_AUTH_DISABLED === 'true' && import.meta.env.DEV

/** Which specific settings are missing, so the UI can name them rather than
 *  listing everything and making the reader check each one. */
export const missingAuthConfig: string[] = [
  ...(url ? [] : ['VITE_SUPABASE_URL']),
  ...(anonKey ? [] : ['VITE_SUPABASE_ANON_KEY']),
]

export const supabase =
  url && anonKey
    ? createClient(url, anonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
        },
      })
    : null

if (!supabase && !AUTH_DISABLED) {
  // Fail loudly at startup rather than with a confusing 401 on first fetch.
  console.error(
    'Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
  )
}
